import React, { useState, useEffect, useCallback } from "react";
import "./SalaryCalculator.css";
import { InputState, ResultsState, TaxBracket } from "../types";

const SalaryCalculator: React.FC = () => {
  // 状态管理
  const [inputs, setInputs] = useState<InputState>({
    bigLevel: "6",
    smallLevel: "5",
    positionLevel: "5",
    base: "6500",
    housingBase: "6500",
    transportSubsidy: "0",
    festivalFee: "0",
    taxDeduction: "0",
    taxDeductionSum: "0",
  });

  const [autoTransport, setAutoTransport] = useState<boolean>(true); // 是否自动计算交通补贴

  const [results, setResults] = useState<ResultsState>({
    basicSalary: 0,
    housingSubsidy: 700,
    targetSalary: 0,
    annuity: 208.34,
    membershipFee: 5,
    pensionInsurance: 0,
    medicalInsurance: 0,
    unemploymentInsurance: 0,
    housingFund: 0,
    grossSalary: 0,
    taxableIncome: 0,
    tax: 0,
    netSalary: 0,
  });

  const [taxBrackets] = useState<TaxBracket[]>([
    { id: 1, range: "不超过36,000元的部分", rate: 0.03, deduction: 0 },
    {
      id: 2,
      range: "超过36,000元至144,000元的部分",
      rate: 0.1,
      deduction: 2520,
    },
    {
      id: 3,
      range: "超过144,000元至300,000元的部分",
      rate: 0.2,
      deduction: 16920,
    },
    {
      id: 4,
      range: "超过300,000元至420,000元的部分",
      rate: 0.25,
      deduction: 31920,
    },
    {
      id: 5,
      range: "超过420,000元至660,000元的部分",
      rate: 0.3,
      deduction: 52920,
    },
    {
      id: 6,
      range: "超过660,000元至960,000元的部分",
      rate: 0.35,
      deduction: 85920,
    },
    { id: 7, range: "超过960,000元的部分", rate: 0.45, deduction: 181920 },
  ]);

  const [activeTaxBracket, setActiveTaxBracket] = useState<number>(1);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // ========== 统一配置 ==========
  // 字段配置接口
  interface FieldConfig {
    id: string;
    name: keyof InputState;
    label: string;
    type: "number" | "text";
    min?: number;
    max?: number;
    step?: number;
    showInParams?: boolean;
    showInTable?: boolean;
    tableSection?: string;
  }

  // 字段配置
  const fieldConfigs: FieldConfig[] = [
    {
      id: "big-level",
      name: "bigLevel",
      label: "大职级",
      type: "number",
      min: 1,
      max: 21,
      showInParams: true,
    },
    {
      id: "small-level",
      name: "smallLevel",
      label: "小职级",
      type: "number",
      min: 1,
      max: 10,
      showInParams: true,
    },
    {
      id: "position-level",
      name: "positionLevel",
      label: "岗位等级",
      type: "number",
      min: 1,
      max: 10,
      showInParams: true,
    },
    {
      id: "base",
      name: "base",
      label: "基数",
      type: "number",
      min: 0,
      step: 100,
      showInParams: true,
    },
    {
      id: "housingBase",
      name: "housingBase",
      label: "公积金基数",
      type: "number",
      min: 0,
      step: 100,
      showInParams: true,
    },
    {
      id: "transport-subsidy",
      name: "transportSubsidy",
      label: "交通补贴",
      type: "number",
      min: 0,
      step: 50,
      showInTable: true,
      tableSection: "发放明细",
    },
    {
      id: "festival-fee",
      name: "festivalFee",
      label: "过节费",
      type: "number",
      min: 0,
      step: 1500,
      showInTable: true,
      tableSection: "发放明细",
    },
    {
      id: "tax-deduction",
      name: "taxDeduction",
      label: "个人附加扣除",
      type: "number",
      min: 0,
      step: 100,
      showInTable: true,
      tableSection: "个人附加扣除",
    },
    {
      id: "tax-deduction-sum",
      name: "taxDeductionSum",
      label: "累计已缴纳税额",
      type: "number",
      min: 0,
      step: 100,
      showInTable: false,
      tableSection: "累计已缴纳税额",
    },
  ];

  // 结果字段配置
  const resultFieldConfigs = [
    {
      label: "基础工资",
      valueKey: "basicSalary" as keyof ResultsState,
      formulaKey: "basicSalary" as const,
      section: "发放明细" as const,
    },
    {
      label: "住房补贴",
      valueKey: "housingSubsidy" as keyof ResultsState,
      formulaKey: "housingSubsidy" as const,
      section: "发放明细" as const,
    },
    {
      label: "目标工资",
      valueKey: "targetSalary" as keyof ResultsState,
      formulaKey: "targetSalary" as const,
      section: "发放明细" as const,
    },
    {
      label: "企业年金个人",
      valueKey: "annuity" as keyof ResultsState,
      formulaKey: "annuity" as const,
      section: "应扣项明细" as const,
    },
    {
      label: "会费",
      valueKey: "membershipFee" as keyof ResultsState,
      formulaKey: "membershipFee" as const,
      section: "应扣项明细" as const,
    },
    {
      label: "养老保险个人",
      valueKey: "pensionInsurance" as keyof ResultsState,
      formulaKey: "pensionInsurance" as const,
      section: "应扣项明细" as const,
    },
    {
      label: "医疗保险个人",
      valueKey: "medicalInsurance" as keyof ResultsState,
      formulaKey: "medicalInsurance" as const,
      section: "应扣项明细" as const,
    },
    {
      label: "失业保险个人",
      valueKey: "unemploymentInsurance" as keyof ResultsState,
      formulaKey: "unemploymentInsurance" as const,
      section: "应扣项明细" as const,
    },
    {
      label: "公积金个人",
      valueKey: "housingFund" as keyof ResultsState,
      formulaKey: "housingFund" as const,
      section: "应扣项明细" as const,
    },
    {
      label: "应缴税金",
      valueKey: "tax" as keyof ResultsState,
      formulaKey: "tax" as const,
      section: "应扣项明细" as const,
    },
  ];

  // 从统一配置生成原有的数组
  const inputFields = fieldConfigs.map((config) => ({
    id: config.id,
    name: config.name,
    label: config.label,
    type: config.type,
    min: config.min,
    max: config.max,
    step: config.step,
  }));

  // 格式化货币显示
  const formatCurrency = (num: number): string => {
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // 格式化数字显示
  const formatNumber = (num: number): string => {
    return num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // 将字符串输入值转换为数字（空字符串视为0）
  const parseInputValue = (value: string | number): number => {
    if (typeof value === "string") {
      if (value === "" || value === null || value === undefined) {
        return 0;
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return value;
  };

  // 根据大职级获取交通补贴（自动模式规则）
  const getTransportByBigLevel = (bigLevel: number): number => {
    if (bigLevel < 5) return 0;
    if (bigLevel <= 6) return 300; // 5-6
    if (bigLevel === 7) return 400;
    if (bigLevel === 8) return 500;
    if (bigLevel === 9) return 1250;
    if (bigLevel <= 12) return 1667; // 10-12
    if (bigLevel <= 16) return 2083; // 13-16
    return 2500; // >=17
  };

  // 格式化输入参数显示
  const inputParams = fieldConfigs
    .filter((config) => config.showInParams)
    .map((config) => ({
      label: config.label,
      valueKey: config.name,
      formatFn: formatNumber,
    }));

  const tableItems = [
    ...resultFieldConfigs.map((item) => ({
      label: item.label,
      valueKey: item.valueKey,
      formulaKey: item.formulaKey,
      source: "results" as const,
      section: item.section,
    })),
    ...fieldConfigs
      .filter((config) => config.showInTable && config.tableSection)
      .map((config) => ({
        label: config.label,
        valueKey: config.name,
        formulaKey: null,
        source: "inputs" as const,
        section: config.tableSection!,
      })),
  ];

  // ========== 其他代码保持不变 ==========
  // 按section分组
  const tableSections = Array.from(
    new Set(tableItems.map((item) => item.section)),
  );

  // 公式提示内容
  const formulaTooltips: { [key: string]: string } = {
    basicSalary: "基础工资根据职级计算",
    housingSubsidy: "住房补贴根据职级计算",
    targetSalary: "目标工资 = ROUND(10000 + 5000/12 × 岗位等级, 0)",
    grossSalary:
      "应发工资 = 基础工资 + 住房补贴 + 目标工资 + 交通补贴 + 过节费",
    netSalary:
      "实发工资 = 应发工资 - 企业年金个人 - 会费 - 养老保险个人 - 医疗保险个人 - 失业保险个人 - 公积金个人 - 应缴税金",
    annuity: "企业年金个人 = 208.34 (固定值)",
    membershipFee: "会费 = 5 (固定值)",
    pensionInsurance: "养老保险个人 = 基数 × 8%",
    medicalInsurance: "医疗保险个人 = 基数 × 2%",
    unemploymentInsurance: "失业保险个人 = 基数 × 0.5%",
    housingFund: "公积金个人 = 公积金基数 × 12%",
    tax: "应缴税金 = (基础工资 + 住房补贴 + 目标工资 + 交通补贴 + 过节费 - 企业年金个人 - 养老保险个人 - 医疗保险个人 - 失业保险个人 - 公积金个人 - 抵税扣除 - 5000) × 对应税率 - 速算扣除数",
    taxableIncome:
      "应纳税所得额 = 基础工资 + 住房补贴 + 目标工资 + 交通补贴 + 过节费 - 企业年金个人 - 养老保险个人 - 医疗保险个人 - 失业保险个人 - 公积金个人 - 抵税扣除 - 5000",
  };

  // 检测是否移动设备
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // 处理输入变化 - 现在支持清空输入框
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs((prev) => ({
      ...prev,
      [name]: value, // 直接保存字符串值，允许空字符串
    }));
  };

  // 重置表单
  const handleReset = () => {
    setInputs({
      bigLevel: "6",
      smallLevel: "5",
      positionLevel: "5",
      base: "6500",
      housingBase: "6500",
      transportSubsidy: "300",
      festivalFee: "0",
      taxDeduction: "0",
      taxDeductionSum: "0",
    });
    setAutoTransport(false); // 重置自动模式
  };

  // 显示/隐藏工具提示
  const toggleTooltip = useCallback(
    (key: string, event: React.MouseEvent) => {
      if (activeTooltip === key) {
        setActiveTooltip(null);
      } else {
        setActiveTooltip(key);

        // 计算提示框位置，确保不会超出屏幕
        const rect = event.currentTarget.getBoundingClientRect();

        // 计算理想位置（在图标下方）
        let left = rect.left;
        let top = rect.bottom + 10;
        if (isMobile) {
          const windowWidth = window.innerWidth;
          // 如果提示框会超出屏幕右侧，则调整到左侧
          if (left > windowWidth / 2) {
            left = 50;
          }

          // 确保左侧不会超出屏幕
          if (left < 50) {
            left = 50;
          }
        }
        setTooltipPosition({ top, left });
      }
    },
    [activeTooltip, isMobile],
  );

  // 关闭工具提示
  const closeTooltip = useCallback(() => {
    setActiveTooltip(null);
  }, []);

  // 点击其他地方关闭工具提示
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        activeTooltip &&
        !(event.target as Element).closest(".formula-tooltip-container")
      ) {
        closeTooltip();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeTooltip, closeTooltip]);

  // 计算工资
  const calculateSalary = useCallback(() => {
    // 解析所有输入值为数字，空字符串视为0
    const parsedInputs = {
      bigLevel: parseInputValue(inputs.bigLevel),
      smallLevel: parseInputValue(inputs.smallLevel),
      positionLevel: parseInputValue(inputs.positionLevel),
      base: parseInputValue(inputs.base),
      housingBase: parseInputValue(inputs.housingBase),
      transportSubsidy: parseInputValue(inputs.transportSubsidy),
      festivalFee: parseInputValue(inputs.festivalFee),
      taxDeduction: parseInputValue(inputs.taxDeduction),
      taxDeductionSum: parseInputValue(inputs.taxDeductionSum),
    };

    const {
      bigLevel,
      smallLevel,
      positionLevel,
      base,
      housingBase,
      festivalFee,
      taxDeduction,
    } = parsedInputs;

    // 交通补贴：根据自动模式决定
    const transportSubsidy = autoTransport
      ? getTransportByBigLevel(bigLevel)
      : parsedInputs.transportSubsidy;

    // 1. 基础工资
    const salaryArray = [
      [830, 890, 950, 1010, 1060],
      [1040, 1110, 1190, 1260, 1330],
      [1300, 1390, 1480, 1580, 1670],
      [1600, 1700, 1800, 1970, 2080],
      [2020, 2130, 2240, 2400, 2550],
      [2510, 2680, 2850, 3030, 3210],
      [3060, 3270, 3480, 3690, 3900],
      [3730, 3940, 4100, 4400, 4700],
      [4500, 4800, 5100, 5500, 5900],
      [5500, 5950, 6300, 6800, 7300],
      [6900, 7400, 7800, 8500, 9100],
      [8600, 9200, 9700, 10500, 11300],
      [10600, 11400, 11900, 13100, 14300],
      [13400, 13400, 14400, 14900, 16400],
      [16600, 17900, 18400, 20200, 22100],
      [20300, 22200, 22900, 24200, 25900],
    ];
    const basicSalary =
      bigLevel === 0 || smallLevel === 0
        ? 0
        : bigLevel <= 16
          ? salaryArray[bigLevel - 1][smallLevel - 1]
          : salaryArray[15][smallLevel - 1];

    // 2. 住房补贴
    const housingArray = [
      0, 0, 500, 500, 700, 700, 900, 1400, 2000, 2500, 2800, 3000, 3600, 3800,
      4200, 4500,
    ];
    const housingSubsidy =
      bigLevel === 0
        ? 0
        : bigLevel <= 16
          ? housingArray[bigLevel - 1]
          : housingArray[15];

    // 3. 目标工资 = ROUND(10000 + 5000/12 * 岗位等级, 0)
    const targetSalary = Math.round(10000 + (5000 / 12) * positionLevel);

    // 4. 企业年金个人 = 208.34
    const annuity = 208.34;

    // 5. 会费 = 5
    const membershipFee = 5;

    // 6. 养老保险个人 = 基数 * 0.08
    const pensionInsurance = base * 0.08;

    // 7. 医疗保险个人 = 基数 * 0.02
    const medicalInsurance = base * 0.02;

    // 8. 失业保险个人 = 基数 * 0.005
    const unemploymentInsurance = base * 0.005;

    // 9. 公积金个人 = 公积金基数 * 0.12
    const housingFund = Math.round(Math.min(housingBase * 0.12, 4297));

    // 10. 应发工资 = 基础工资 + 住房补贴 + 目标工资 + 交通补贴 + 过节费
    const grossSalary =
      basicSalary +
      housingSubsidy +
      targetSalary +
      transportSubsidy +
      festivalFee;

    // 11. 应纳税所得额
    const taxableIncome =
      basicSalary +
      housingSubsidy +
      targetSalary +
      transportSubsidy +
      festivalFee -
      annuity -
      pensionInsurance -
      medicalInsurance -
      unemploymentInsurance -
      housingFund -
      taxDeduction -
      5000;

    // 12. 计算应缴税金（根据税率表）
    let tax = 0;
    let activeBracket = 1;

    if (taxableIncome > 0) {
      if (taxableIncome <= 36000) {
        tax = taxableIncome * 0.03;
        activeBracket = 1;
      } else if (taxableIncome <= 144000) {
        tax = taxableIncome * 0.1 - 2520;
        activeBracket = 2;
      } else if (taxableIncome <= 300000) {
        tax = taxableIncome * 0.2 - 16920;
        activeBracket = 3;
      } else if (taxableIncome <= 420000) {
        tax = taxableIncome * 0.25 - 31920;
        activeBracket = 4;
      } else if (taxableIncome <= 660000) {
        tax = taxableIncome * 0.3 - 52920;
        activeBracket = 5;
      } else if (taxableIncome <= 960000) {
        tax = taxableIncome * 0.35 - 85920;
        activeBracket = 6;
      } else {
        tax = taxableIncome * 0.45 - 181920;
        activeBracket = 7;
      }
    }

    // 确保税金不为负数
    tax = Math.max(tax, 0);

    // 13. 实发工资 = 应发工资 - 企业年金个人 - 会费 - 养老保险个人 - 医疗保险个人 - 失业保险个人 - 公积金个人 - 应缴税金
    const netSalary =
      grossSalary -
      annuity -
      membershipFee -
      pensionInsurance -
      medicalInsurance -
      unemploymentInsurance -
      housingFund -
      tax;

    // 更新结果状态
    setResults({
      basicSalary,
      housingSubsidy,
      targetSalary,
      annuity,
      membershipFee,
      pensionInsurance,
      medicalInsurance,
      unemploymentInsurance,
      housingFund,
      grossSalary,
      taxableIncome,
      tax,
      netSalary,
    });

    setActiveTaxBracket(activeBracket);
  }, [inputs, autoTransport]);

  // 当输入变化时重新计算
  useEffect(() => {
    calculateSalary();
  }, [calculateSalary]);

  // 公式提示组件 Props 接口
  interface FormulaTooltipProps {
    formulaKey: string;
    children: React.ReactNode;
  }

  // 公式提示组件
  const FormulaTooltip: React.FC<FormulaTooltipProps> = ({
    formulaKey,
    children,
  }) => {
    const handleIconClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleTooltip(formulaKey, e);
    };

    return (
      <div className="formula-tooltip-container">
        <div className="formula-label-wrapper">
          {children}
          <span
            className="formula-question-icon"
            onClick={handleIconClick}
            title="点击查看计算公式"
          >
            <i className="fas fa-question-circle"></i>
          </span>
        </div>

        {activeTooltip === formulaKey && (
          <div
            className="formula-tooltip"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              width: isMobile ? "50vw" : "320px",
              maxWidth: "320px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="formula-content">
              <strong>计算公式：</strong>
              <p>{formulaTooltips[formulaKey]}</p>
            </div>
            <div className="formula-close" onClick={closeTooltip}>
              <i className="fas fa-times"></i>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染输入字段 - 现在使用字符串值
  const renderInputFields = () => {
    return inputFields.map((field) => (
      <div className="input-group-inline" key={field.id}>
        <label htmlFor={field.id}>{field.label}</label>
        <input
          type="text" // 改为text类型以支持清空
          inputMode="decimal" // 移动设备上显示数字键盘
          pattern="[0-9.]*" // 允许数字和小数点
          id={field.id}
          name={field.name}
          min={field.min}
          max={field.max}
          step={field.step}
          value={inputs[field.name as keyof InputState] || ""} // 显示字符串值，空字符串显示为空
          onChange={handleInputChange}
          placeholder="0"
        />
      </div>
    ));
  };

  // 渲染输入参数 - 解析为数字后显示
  const renderInputParams = () => {
    return inputParams.map((param, index) => {
      const value = parseInputValue(inputs[param.valueKey as keyof InputState]);
      return (
        <div className="detail-item" key={index}>
          <div className="detail-label">{param.label}</div>
          <div className="detail-value">{param.formatFn(value)}</div>
        </div>
      );
    });
  };

  // 渲染税率表行
  const renderTaxBracketRows = () => {
    return taxBrackets.map((bracket) => (
      <tr
        key={bracket.id}
        className={activeTaxBracket === bracket.id ? "highlight-row" : ""}
      >
        <td>{bracket.id}</td>
        <td>{bracket.range}</td>
        <td>{bracket.rate * 100}%</td>
        <td>{bracket.deduction.toLocaleString()}</td>
      </tr>
    ));
  };

  // 渲染表格行（根据section分组）
  const renderTableRows = (section: string) => {
    return tableItems
      .filter((item) => item.section === section)
      .map((item, index) => {
        let value: number;
        if (item.source === "results") {
          value = results[item.valueKey as keyof ResultsState];
        } else {
          // 对于交通补贴，根据自动模式决定实际值
          if (item.valueKey === "transportSubsidy" && autoTransport) {
            const bigLevelNum = parseInputValue(inputs.bigLevel);
            value = getTransportByBigLevel(bigLevelNum);
          } else {
            value = parseInputValue(inputs[item.valueKey as keyof InputState]);
          }
        }

        return (
          <tr key={index}>
            <td>
              {item.formulaKey ? (
                <FormulaTooltip formulaKey={item.formulaKey}>
                  {item.label}
                </FormulaTooltip>
              ) : (
                item.label
              )}
            </td>
            <td className="amount-cell">{formatCurrency(value)}</td>
          </tr>
        );
      });
  };

  // 渲染明细表格
  const renderDetailTables = () => {
    return tableSections.map((section, sectionIndex) => (
      <div className="breakdown-section" key={sectionIndex}>
        <h3 className="details-title">{section}</h3>
        <div className="breakdown-tables">
          <div className="breakdown-table-container">
            <div className="table-scroll-wrapper">
              <table className="breakdown-table">
                <colgroup>
                  <col style={{ width: "80%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>项目</th>
                    <th>金额(元)</th>
                  </tr>
                </thead>
                <tbody>{renderTableRows(section)}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="salary-calculator-container">
      <header className="header">
        <h1>
          <i className="fas fa-calculator"></i> 工资计算器
        </h1>
        {/* <p>根据大职级、小职级、岗位等级等信息计算工资明细</p> */}
      </header>

      <div className="input-section">
        <h2 className="section-title">
          <i className="fas fa-edit"></i> 输入工资信息
        </h2>

        <div className="form-row">{renderInputFields().slice(0, 3)}</div>

        <div className="form-row">
          {/* 前两个字段：基数、公积金基数 */}
          {renderInputFields().slice(3, 5)}
          {/* 交通补贴字段（特殊处理，带复选框） */}
          <div className="input-group-inline transport-group">
            <label htmlFor="transport-subsidy">交通补贴</label>
            <input
              type="text"
              inputMode="decimal"
              pattern="[0-9.]*"
              id="transport-subsidy"
              name="transportSubsidy"
              min={0}
              step={50}
              value={
                autoTransport
                  ? getTransportByBigLevel(
                      parseInt(String(inputs.bigLevel) || "0"),
                    ).toString()
                  : inputs.transportSubsidy
              }
              onChange={handleInputChange}
              placeholder="0"
              disabled={autoTransport}
            />
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={autoTransport}
                onChange={(e) => setAutoTransport(e.target.checked)}
              />
            </label>
          </div>
        </div>

        <div className="form-row">{renderInputFields().slice(6)}</div>

        <div className="button-group">
          <button className="reset-btn" onClick={handleReset}>
            <i className="fas fa-redo"></i> 重置
          </button>
        </div>
      </div>

      <div className="result-section">
        <h2 className="section-title">
          <i className="fas fa-chart-bar"></i> 计算结果
        </h2>

        <div className="result-summary">
          <div className="summary-card highlight">
            <FormulaTooltip formulaKey="grossSalary">
              <div className="summary-label">应发工资</div>
            </FormulaTooltip>
            <div className="summary-value">
              {formatCurrency(results.grossSalary)}
            </div>
          </div>

          <div className="summary-card highlight">
            <FormulaTooltip formulaKey="netSalary">
              <div className="summary-label">实发工资</div>
            </FormulaTooltip>
            <div className="summary-value">
              {formatCurrency(results.netSalary)}
            </div>
          </div>
        </div>

        <div className="result-details">
          <h3 className="details-title">输入参数</h3>
          <div className="details-grid">{renderInputParams()}</div>
        </div>

        {renderDetailTables()}

        <div className="tax-info">
          <h3>
            <i className="fas fa-file-invoice-dollar"></i> 个人所得税税率表
          </h3>
          <div className="table-scroll-wrapper">
            <table className="tax-table">
              <thead>
                <tr>
                  <th>级数</th>
                  <th>累计预扣预缴应纳税所得额</th>
                  <th>预扣税率</th>
                  <th>速算扣除数</th>
                </tr>
              </thead>
              <tbody>{renderTaxBracketRows()}</tbody>
            </table>
          </div>
        </div>
      </div>

      <footer>
        <p>
          © 2026 Zixing Wang. All rights reserved. |
          本工具仅供参考，实际工资以公司发放为准
        </p>
      </footer>
    </div>
  );
};

export default SalaryCalculator;
